<!-- src/lib/components/PersonasView.svelte -->
<script lang="ts">
  import { pmStore } from '../stores/pm.svelte.ts';
  import { api } from '../api.ts';
  import { toast } from '../stores/toast.svelte.ts';
  import type { Persona } from '../types.ts';

  let personas = $state<Persona[]>([]);
  let loading = $state(false);
  let creating = $state(false);
  let selectedId = $state<string | null>(null);
  let showCreateForm = $state(false);
  let activeWorkspaceId = $state<string | null>(null);

  let newName = $state('');
  let newDesc = $state('');
  let newTier = $state('tier-1');
  let newPrompt = $state('');

  let editName = $state('');
  let editDesc = $state('');
  let editTier = $state('tier-1');
  let editPrompt = $state('');

  let testPrompt = $state('Say hello and describe your role in one sentence.');
  let testRunning = $state(false);
  let testOpen = $state(false);

  const selectedPersona = $derived(personas.find((persona) => persona.id === selectedId) ?? null);

  $effect(() => {
    const wsId = pmStore.activeWorkspaceId;
    if (!wsId) {
      activeWorkspaceId = null;
      personas = [];
      selectedId = null;
      showCreateForm = false;
      return;
    }
    if (activeWorkspaceId === wsId) return;
    activeWorkspaceId = wsId;
    selectedId = null;
    showCreateForm = false;
    void loadPersonas(wsId);
  });

  $effect(() => {
    if (!selectedPersona) return;
    editName = selectedPersona.name;
    editDesc = selectedPersona.description ?? '';
    editTier = selectedPersona.preferred_tier;
    editPrompt = selectedPersona.system_prompt ?? '';
    testPrompt = 'Say hello and describe your role in one sentence.';
    testOpen = false;
  });

  async function loadPersonas(wsId: string): Promise<void> {
    loading = true;
    try {
      const list = await api<Persona[]>(`/api/v1/workspaces/${wsId}/personas`);
      personas = list;
      if (selectedId && list.some((persona) => persona.id === selectedId)) return;
      selectedId = list[0]?.id ?? null;
    } catch {
      personas = [];
      selectedId = null;
    } finally {
      loading = false;
    }
  }

  async function createPersona(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    if (!newName.trim() || !pmStore.activeWorkspaceId) return;
    creating = true;
    try {
      const persona = await api<Persona>(`/api/v1/workspaces/${pmStore.activeWorkspaceId}/personas`, {
        method: 'POST',
        body: JSON.stringify({
          name: newName.trim(),
          description: newDesc.trim() || null,
          system_prompt: newPrompt.trim() || null,
          preferred_tier: newTier,
        }),
      });
      personas = [...personas, persona];
      selectedId = persona.id;
      showCreateForm = false;
      newName = '';
      newDesc = '';
      newTier = 'tier-1';
      newPrompt = '';
      toast.push({ title: 'Persona created', body: persona.name, kind: 'success' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.push({ title: 'Create failed', body: msg, kind: 'error' });
    } finally {
      creating = false;
    }
  }

  async function saveField(field: 'name' | 'description' | 'system_prompt' | 'preferred_tier', value: string): Promise<void> {
    const wsId = pmStore.activeWorkspaceId;
    const persona = selectedPersona;
    if (!wsId || !persona) return;

    const trimmed = value.trim();
    if (field === 'name' && !trimmed) {
      editName = persona.name;
      return;
    }

    const payloadValue = field === 'preferred_tier' || field === 'name'
      ? trimmed
      : (trimmed || null);

    personas = personas.map((item) => item.id === persona.id ? {
      ...item,
      [field]: payloadValue,
    } : item);

    try {
      await api(`/api/v1/workspaces/${wsId}/personas/${persona.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ [field]: payloadValue }),
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.push({ title: 'Save failed', body: msg, kind: 'error' });
      await loadPersonas(wsId);
    }
  }

  async function deletePersona(id: string): Promise<void> {
    const wsId = pmStore.activeWorkspaceId;
    if (!wsId) return;
    if (!window.confirm('Delete this persona?')) return;
    try {
      await api(`/api/v1/workspaces/${wsId}/personas/${id}`, { method: 'DELETE' });
      const next = personas.filter((persona) => persona.id !== id);
      personas = next;
      if (selectedId === id) selectedId = next[0]?.id ?? null;
      toast.push({ title: 'Persona deleted', body: '', kind: 'warning' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.push({ title: 'Delete failed', body: msg, kind: 'error' });
    }
  }

  async function dispatchTestRun(): Promise<void> {
    if (!selectedId || !pmStore.activeWorkspaceId || testRunning || !testPrompt.trim()) return;
    testRunning = true;
    try {
      await api('/api/v1/tasks', {
        method: 'POST',
        body: JSON.stringify({
          prompt: testPrompt.trim(),
          persona_id: selectedId,
          workspace_id: pmStore.activeWorkspaceId,
        }),
      });
      toast.push({ title: 'Test agent dispatched', body: selectedPersona?.name ?? '', kind: 'success' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.push({ title: 'Dispatch failed', body: msg, kind: 'error' });
    } finally {
      setTimeout(() => {
        testRunning = false;
      }, 3000);
    }
  }

  function tierLabel(tier: string): string {
    switch (tier) {
      case 'tier-1': return 'Tier 1 — Claude Code';
      case 'tier-2': return 'Tier 2 — OpenRouter';
      case 'tier-3': return 'Tier 3 — Ollama';
      default: return tier;
    }
  }

  function tierCostLabel(tier: string): string {
    switch (tier) {
      case 'tier-1': return 'Tier 1 — Claude Code (~$0.015/task)';
      case 'tier-2': return 'Tier 2 — OpenRouter (~$0.002/task)';
      case 'tier-3': return 'Tier 3 — Ollama (local, free)';
      default: return tier;
    }
  }

  function tierShort(tier: string): string {
    switch (tier) {
      case 'tier-1': return 'T1';
      case 'tier-2': return 'T2';
      case 'tier-3': return 'T3';
      default: return tier;
    }
  }

  function tierClass(tier: string): string {
    switch (tier) {
      case 'tier-1': return 'badge-purple';
      case 'tier-2': return 'badge-blue';
      case 'tier-3': return 'badge-amber';
      default: return 'badge-dim';
    }
  }
</script>

<div class="personas-view">
  {#if !pmStore.activeWorkspaceId}
    <div class="empty-state">Select a workspace to manage personas.</div>
  {:else}
    <div class="personas-layout">
      <aside class="personas-list">
        <div class="list-header">
          <span class="list-title">Personas</span>
          <button
            class="new-btn"
            onclick={() => { showCreateForm = true; selectedId = null; }}
            title="New persona"
            aria-label="New persona"
          >+</button>
        </div>

        {#if loading}
          <p class="hint">Loading…</p>
        {:else if personas.length === 0 && !showCreateForm}
          <p class="hint">No personas yet.</p>
        {/if}

        <ul class="persona-list-items">
          {#each personas as persona (persona.id)}
            <li>
              <button
                class="persona-list-btn"
                class:active={selectedId === persona.id}
                onclick={() => { selectedId = persona.id; showCreateForm = false; }}
              >
                <div class="persona-list-copy">
                  <span class="plb-name">{persona.name}</span>
                  {#if persona.description}
                    <span class="plb-desc">{persona.description}</span>
                  {/if}
                </div>
                <span class="plb-badge {tierClass(persona.preferred_tier)}">{tierShort(persona.preferred_tier)}</span>
              </button>
            </li>
          {/each}
        </ul>
      </aside>

      <div class="persona-detail">
        {#if showCreateForm}
          <div class="detail-header">
            <h2 class="detail-title">New Persona</h2>
          </div>
          <form class="detail-form" onsubmit={createPersona}>
            <label class="field-label">Name
              <input class="field-input" bind:value={newName} placeholder="e.g. Backend Agent" required />
            </label>
            <label class="field-label">Description
              <input class="field-input" bind:value={newDesc} placeholder="Optional description" />
            </label>
            <label class="field-label">Tier
              <select class="field-input" bind:value={newTier}>
                <option value="tier-1">Tier 1 — Claude Code (~$0.015/task)</option>
                <option value="tier-2">Tier 2 — OpenRouter (~$0.002/task)</option>
                <option value="tier-3">Tier 3 — Ollama (local, free)</option>
              </select>
            </label>
            <label class="field-label">System prompt
              <textarea class="field-input field-textarea" bind:value={newPrompt} placeholder="Optional system prompt override…" rows={10}></textarea>
            </label>
            <div class="form-actions">
              <button type="submit" class="btn-primary" disabled={creating || !newName.trim()}>
                {creating ? 'Creating…' : 'Create Persona'}
              </button>
              <button
                type="button"
                class="btn-secondary"
                onclick={() => { showCreateForm = false; selectedId = personas[0]?.id ?? null; }}
              >Cancel</button>
            </div>
          </form>

        {:else if selectedPersona}
          <div class="detail-header">
            <input
              class="detail-name-input"
              bind:value={editName}
              onblur={() => void saveField('name', editName)}
              aria-label="Persona name"
            />
            <span class="detail-tier-badge {tierClass(editTier)}">{tierLabel(editTier)}</span>
            <button class="delete-btn" onclick={() => void deletePersona(selectedPersona.id)} title="Delete persona">Delete</button>
          </div>

          <div class="detail-body">
            <label class="field-label">Description
              <input
                class="field-input"
                bind:value={editDesc}
                placeholder="Optional description"
                onblur={() => void saveField('description', editDesc)}
              />
            </label>
            <label class="field-label">Tier
              <select
                class="field-input"
                bind:value={editTier}
                onchange={() => void saveField('preferred_tier', editTier)}
              >
                <option value="tier-1">Tier 1 — Claude Code (~$0.015/task)</option>
                <option value="tier-2">Tier 2 — OpenRouter (~$0.002/task)</option>
                <option value="tier-3">Tier 3 — Ollama (local, free)</option>
              </select>
            </label>
            <label class="field-label">System prompt
              <textarea
                class="field-input field-textarea"
                bind:value={editPrompt}
                placeholder="No system prompt — default agent behavior"
                rows={14}
                onblur={() => void saveField('system_prompt', editPrompt)}
              ></textarea>
            </label>

            <div class="test-run-section">
              <button
                class="test-run-toggle"
                onclick={() => { testOpen = !testOpen; }}
                aria-expanded={testOpen}
              >
                <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.5" class:rotated={testOpen}>
                  <path d="M6 4l4 4-4 4" />
                </svg>
                Test run
              </button>
              {#if testOpen}
                <div class="test-run-body">
                  <label class="field-label">Test prompt
                    <textarea
                      class="field-input field-textarea"
                      bind:value={testPrompt}
                      rows={4}
                      placeholder="Enter a test prompt…"
                    ></textarea>
                  </label>
                  <button
                    class="btn-primary test-dispatch-btn"
                    onclick={() => void dispatchTestRun()}
                    disabled={testRunning || !testPrompt.trim()}
                  >
                    {testRunning ? 'Dispatching…' : '▶ Dispatch test run'}
                  </button>
                </div>
              {/if}
            </div>
          </div>

        {:else}
          <div class="detail-empty">
            <p>Select a persona to view or edit it.</p>
            <button class="btn-primary" onclick={() => { showCreateForm = true; }}>Create your first persona</button>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .personas-view {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .empty-state {
    color: oklch(0.45 0.04 310 / 0.55);
    font-size: 0.875rem;
    padding: 2rem;
    text-align: center;
  }
  .personas-layout {
    display: flex;
    flex: 1;
    overflow: hidden;
  }
  .personas-list {
    width: 300px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    border-right: 1px solid oklch(0.30 0.05 275 / 0.20);
    overflow: hidden;
  }
  .list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.85rem 1rem 0.6rem 1rem;
    border-bottom: 1px solid oklch(0.25 0.04 275 / 0.15);
    flex-shrink: 0;
  }
  .list-title {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    color: oklch(0.55 0.04 310 / 0.60);
  }
  .new-btn {
    width: 22px;
    height: 22px;
    background: oklch(0.72 0.28 310 / 0.10);
    border: 1px solid oklch(0.72 0.28 310 / 0.25);
    color: oklch(0.80 0.26 310);
    border-radius: 5px;
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 100ms ease;
  }
  .new-btn:hover {
    background: oklch(0.72 0.28 310 / 0.20);
  }
  .persona-list-items {
    list-style: none;
    padding: 0.4rem 0;
    margin: 0;
    overflow-y: auto;
    flex: 1;
  }
  .persona-list-items::-webkit-scrollbar {
    width: 5px;
  }
  .persona-list-items::-webkit-scrollbar-thumb {
    background: oklch(0.30 0.05 275 / 0.35);
    border-radius: 3px;
  }
  .persona-list-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    background: transparent;
    border: none;
    padding: 0.55rem 1rem;
    cursor: pointer;
    gap: 0.75rem;
    transition: background 80ms ease;
  }
  .persona-list-btn:hover {
    background: oklch(0.72 0.28 310 / 0.06);
  }
  .persona-list-btn.active {
    background: oklch(0.72 0.28 310 / 0.12);
  }
  .persona-list-copy {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.18rem;
    flex: 1;
  }
  .plb-name {
    font-size: 0.82rem;
    color: oklch(0.85 0.06 310);
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .plb-desc {
    font-size: 0.68rem;
    color: oklch(0.50 0.04 310 / 0.70);
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .persona-list-btn.active .plb-name {
    color: oklch(0.96 0.10 310);
  }
  .plb-badge {
    font-size: 0.55rem;
    font-weight: 700;
    padding: 0.1rem 0.35rem;
    border-radius: 3px;
    flex-shrink: 0;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .persona-detail {
    flex: 1;
    overflow-y: auto;
    padding: 1.25rem 1.75rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .persona-detail::-webkit-scrollbar {
    width: 6px;
  }
  .persona-detail::-webkit-scrollbar-thumb {
    background: oklch(0.30 0.05 275 / 0.35);
    border-radius: 3px;
  }
  .detail-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .detail-name-input {
    flex: 1;
    min-width: 180px;
    background: transparent;
    border: none;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.25);
    color: oklch(0.94 0.08 310);
    font-size: 1.05rem;
    font-weight: 600;
    padding: 0.15rem 0;
    outline: none;
    transition: border-color 100ms ease;
    font-family: inherit;
  }
  .detail-name-input:focus {
    border-bottom-color: oklch(0.72 0.28 310 / 0.55);
  }
  .detail-title {
    font-size: 1.05rem;
    font-weight: 600;
    color: oklch(0.94 0.08 310);
    margin: 0;
    flex: 1;
  }
  .detail-tier-badge {
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    padding: 0.2rem 0.6rem;
    border-radius: 4px;
    text-transform: uppercase;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .delete-btn {
    background: transparent;
    border: 1px solid oklch(0.62 0.22 25 / 0.25);
    color: oklch(0.62 0.22 25 / 0.65);
    font-size: 0.72rem;
    padding: 0.2rem 0.6rem;
    border-radius: 4px;
    cursor: pointer;
    font-family: inherit;
    transition: background 100ms ease, color 100ms ease;
    margin-left: auto;
  }
  .delete-btn:hover {
    background: oklch(0.62 0.22 25 / 0.12);
    color: oklch(0.72 0.22 25);
  }
  .detail-body {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .detail-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    color: oklch(0.45 0.04 310 / 0.55);
    font-size: 0.82rem;
  }
  .detail-form {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
  }
  .field-label {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    font-size: 0.70rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: oklch(0.55 0.04 310 / 0.60);
  }
  .field-input {
    background: oklch(0.12 0.02 275 / 0.60);
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
    color: oklch(0.88 0.06 310);
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    font-size: 0.85rem;
    outline: none;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 100ms ease;
    font-family: inherit;
  }
  .field-input:focus {
    border-color: oklch(0.72 0.28 310 / 0.40);
  }
  .field-input option {
    background: oklch(0.10 0.018 275);
    color: oklch(0.88 0.06 310);
  }
  .field-textarea {
    resize: vertical;
    line-height: 1.5;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.78rem;
    color: oklch(0.82 0.26 310 / 0.85);
  }
  .form-actions {
    display: flex;
    gap: 0.5rem;
  }
  .btn-primary {
    background: oklch(0.72 0.28 310 / 0.10);
    border: 1px solid oklch(0.72 0.28 310 / 0.30);
    color: oklch(0.80 0.26 310);
    padding: 0.45rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.82rem;
    font-weight: 500;
    font-family: inherit;
    transition: background 100ms ease;
  }
  .btn-primary:hover:not(:disabled) {
    background: oklch(0.72 0.28 310 / 0.20);
  }
  .btn-primary:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .btn-secondary {
    background: transparent;
    border: 1px solid oklch(0.30 0.05 275 / 0.30);
    color: oklch(0.55 0.04 310 / 0.70);
    padding: 0.45rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.82rem;
    font-family: inherit;
    transition: background 100ms ease;
  }
  .btn-secondary:hover {
    background: oklch(0.14 0.02 275 / 0.60);
  }
  .test-run-section {
    border-top: 1px solid oklch(0.25 0.04 275 / 0.20);
    padding-top: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }
  .test-run-toggle {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    background: transparent;
    border: none;
    color: oklch(0.60 0.12 265 / 0.80);
    font-size: 0.72rem;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    font-family: inherit;
    transition: color 100ms ease;
  }
  .test-run-toggle:hover {
    color: oklch(0.75 0.18 265);
  }
  .test-run-toggle svg {
    transition: transform 150ms ease;
  }
  .test-run-toggle svg.rotated {
    transform: rotate(90deg);
  }
  .test-run-body {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }
  .test-dispatch-btn {
    align-self: flex-start;
    background: oklch(0.55 0.20 265 / 0.10);
    border-color: oklch(0.55 0.20 265 / 0.30);
    color: oklch(0.65 0.20 265);
  }
  .test-dispatch-btn:hover:not(:disabled) {
    background: oklch(0.55 0.20 265 / 0.20);
  }
  .badge-purple {
    background: oklch(0.72 0.28 310 / 0.12);
    color: oklch(0.80 0.26 310);
    border: 1px solid oklch(0.74 0.28 310 / 0.26);
  }
  .badge-blue {
    background: oklch(0.55 0.20 265 / 0.14);
    color: oklch(0.65 0.20 265);
    border: 1px solid oklch(0.55 0.20 265 / 0.22);
  }
  .badge-amber {
    background: oklch(0.78 0.16 85 / 0.10);
    color: oklch(0.78 0.16 85);
    border: 1px solid oklch(0.78 0.16 85 / 0.22);
  }
  .badge-dim {
    background: oklch(0.20 0.03 275 / 0.50);
    color: oklch(0.50 0.03 310 / 0.60);
    border: 1px solid oklch(0.30 0.05 275 / 0.20);
  }
  .hint {
    font-size: 0.78rem;
    color: oklch(0.45 0.04 310 / 0.55);
    margin: 0.5rem 1rem;
    font-style: italic;
  }
  @media (max-width: 960px) {
    .personas-layout {
      flex-direction: column;
    }
    .personas-list {
      width: 100%;
      border-right: none;
      border-bottom: 1px solid oklch(0.30 0.05 275 / 0.20);
      max-height: 260px;
    }
  }
</style>
